import { useEffect, useState } from "react";

// FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

// Utils
import { useSettings } from "context/Settings.context";

// Components
import Data from "./Pages/Data/Data";
import Admin from "./Pages/Admin/Admin";
import Dashboards from "./Pages/Dashboards/Dashboards";
import Fees from "./Pages/Fees/Fees";
import Docs from "./Pages/Docs/Docs";
import Help from "./Pages/Help/Help";
import DailyRecs from "./Pages/DailyRecs/DailyRecs";
import Company from "./Pages/Company/Company";
import Portfolio from "./Pages/Portfolio/Portfolio";
import AskAda from "./Pages/AskAda/AskAda";
import GrowthVoting from "./Pages/GrowthVoting/GrowthVoting";
import MissionControl from "./Pages/MissionControl/MissionControl";
import Reporting from "./Pages/Reporting/Reporting";

// Styles
import SubMenuContainer from "./SubMenu.styles";

type MenuPageType =
    | "data"
    | "fees"
    | "dailyrecs"
    | "docs"
    | "help"
    | "admin"
    | "dashboards"
    | "portfolio"
    | "company"
    | "askAda"
    | "growthVoting"
    | "missionControl"
    | "reporting"
    | "add";

export default function SubMenu() {
    // #######################
    //    Menu Page Control
    // #######################
    const { route, hoverMenuPage, setHoverMenuPage, hoverMenuClearTimeoutRef } =
        useSettings();

    const [menuPage, setMenuPage] = useState<MenuPageType>("add");

    useEffect(() => {
        if (route.startsWith("/data")) setMenuPage("data");
        if (route.startsWith("/docs")) setMenuPage("docs");
        if (route.startsWith("/help")) setMenuPage("help");
        if (route.startsWith("/admin")) setMenuPage("admin");
        if (route.startsWith("/dashboards")) setMenuPage("dashboards");
        if (route.startsWith("/fees")) setMenuPage("fees");
        if (route.startsWith("/reconciliation")) setMenuPage("dailyrecs");
        if (route.startsWith("/portfolio")) setMenuPage("portfolio");
        if (route.startsWith("/company")) setMenuPage("company");
        if (route.startsWith("/ask-ada")) setMenuPage("askAda");
        if (route.startsWith("/growth-voting")) setMenuPage("growthVoting");
        if (route.startsWith("/mission-control")) setMenuPage("missionControl");
        if (route.startsWith("/reporting")) setMenuPage("reporting");
    }, [route]);

    // Use hover menu page if set, otherwise use route-based menu page
    const activeMenuPage: MenuPageType =
        (hoverMenuPage as MenuPageType) || menuPage;

    const handleSubMenuMouseEnter = () => {
        // Cancel any pending timeout from MenuItem
        if (hoverMenuClearTimeoutRef.current) {
            clearTimeout(hoverMenuClearTimeoutRef.current);
            hoverMenuClearTimeoutRef.current = null;
        }
    };

    const handleSubMenuMouseLeave = () => {
        // Clear hover menu page immediately when leaving submenu
        if (hoverMenuClearTimeoutRef.current) {
            clearTimeout(hoverMenuClearTimeoutRef.current);
            hoverMenuClearTimeoutRef.current = null;
        }
        setHoverMenuPage(null);
    };

    // #######################
    // Menu Width and Resizing
    // #######################
    const { subMenuMode, setSubMenuMode, subMenuWidth, setSubMenuWidth } =
        useSettings();
    const [isDragging, setIsDragging] = useState(false);

    const startDrag = (e: React.MouseEvent) => {
        setIsDragging(true);
        window.addEventListener("mouseup", stopDrag, false);
        window.addEventListener("mousemove", updateWidth, false);
    };

    const stopDrag = (e: any) => {
        window.removeEventListener("mouseup", stopDrag, false);
        window.removeEventListener("mousemove", updateWidth, false);
        setIsDragging(false);
    };

    const updateWidth = (e: MouseEvent) => {
        if (e.clientX > 80 && e.clientX < 600) {
            setSubMenuWidth(e.clientX - 70);
        } else if (e.clientX > 600) {
            setSubMenuWidth(600);
        } else {
            setSubMenuWidth(10);
        }
    };

    const closeMenu = () => {
        setSubMenuMode("close");
        setTimeout(() => {
            setSubMenuWidth(15);
        }, 200);
    };

    const openMenu = () => {
        setSubMenuMode("open");
        setTimeout(() => {
            setSubMenuWidth(250);
        }, 200);
    };

    useEffect(() => {
        // If stopped dragging and menu is too small, automatically minimise menu
        if (subMenuWidth < 200 && !isDragging) {
            closeMenu();
        } else {
            setSubMenuMode("init");
        }
    }, [subMenuWidth, isDragging]);

    return (
        <SubMenuContainer
            id={"subMenu"}
            className={subMenuMode}
            style={{ minWidth: subMenuWidth, maxWidth: subMenuWidth }}
            // @ts-ignore
            width={subMenuWidth}
            onMouseEnter={handleSubMenuMouseEnter}
            onMouseLeave={handleSubMenuMouseLeave}
        >
            <div
                className={"handle " + isDragging}
                onMouseDown={startDrag}
                // onMouseUpCapture={stopDrag}
            />

            <div
                className={"minimiseBtn " + subMenuMode}
                onClick={() => (subMenuWidth === 15 ? openMenu() : closeMenu())}
            >
                <FontAwesomeIcon
                    icon={subMenuWidth === 15 ? faChevronRight : faChevronLeft}
                    fixedWidth
                />
            </div>

            {
                {
                    data: <Data />,
                    fees: <Fees />,
                    dailyrecs: <DailyRecs />,
                    docs: <Docs />,
                    help: <Help />,
                    admin: <Admin />,
                    dashboards: <Dashboards />,
                    portfolio: <Portfolio />,
                    company: <Company />,
                    askAda: <AskAda />,
                    growthVoting: <GrowthVoting />,
                    missionControl: <MissionControl />,
                    reporting: <Reporting />,
                    add: null,
                }[activeMenuPage]
            }
        </SubMenuContainer>
    );
}
